import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  appendVideoconsultationSummaryToParams,
  buildVideoconsultationRecommendationCopy,
  formatVideoconsultationSummaryLabel,
  readVideoconsultationSummaryFromQuery,
} from './videoconsultationSummary';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #eef3f7 0%, #dce4eb 100%)',
    padding: '40px 16px 64px',
  },
  panel: {
    maxWidth: 980,
    margin: '0 auto',
  },
  card: {
    borderRadius: 20,
    boxShadow: '0 18px 40px rgba(0, 45, 85, 0.10)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  summaryCard: {
    borderRadius: 18,
    background: '#f5f8fb',
  },
  dialogHeader: {
    borderBottom: '1px solid #e6ebf0',
    padding: '22px 28px 18px',
    background: '#ffffff',
  },
  dialogBody: {
    padding: 28,
  },
  sectionLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  selectWrapper: {
    marginBottom: 24,
    position: 'relative',
  },
  selectIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#fb741a',
    pointerEvents: 'none',
  },
  selectField: {
    width: '100%',
    padding: '10px 12px 10px 34px',
    fontSize: 14,
    border: '1px solid #ddd',
    borderRadius: 14,
    fontFamily: 'Roboto, sans-serif',
    color: '#002D55',
    backgroundColor: '#fff',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: 'none',
    boxSizing: 'border-box',
  },
  dateHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  dateNavButtons: {
    display: 'flex',
    gap: 4,
  },
  navButton: {
    minWidth: 32,
    borderRadius: 18,
    color: '#002D55',
    border: '1px solid #ddd',
    padding: '2px 8px',
  },
  dateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 8,
    marginBottom: 24,
    padding: 12,
    backgroundColor: '#f5f5f5',
    borderRadius: 18,
  },
  dateButton: {
    backgroundColor: '#fff',
    color: '#002D55',
    border: '1px solid #ddd',
    padding: '8px 4px',
    borderRadius: 18,
    minHeight: 60,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 400,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  selectedDateButton: {
    backgroundColor: '#002D55',
    color: '#fff',
    border: 'none',
    fontWeight: 600,
  },
  disabledDateButton: {
    color: '#ccc',
    opacity: 0.4,
    cursor: 'default',
  },
  dateNumber: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
  },
  dateLabel: {
    fontSize: 10,
    marginTop: 4,
    lineHeight: 1,
  },
  availabilityDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    marginTop: 6,
    background: '#fb741a',
  },
  timeGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 24,
  },
  primaryButton: {
    background: '#fb741a',
    color: '#fff',
    fontWeight: 600,
    borderRadius: 18,
    width: '49%',
    '&:hover': {
      background: '#002D55',
    },
  },
  secondaryButton: {
    borderColor: '#002D55',
    color: '#002D55',
    fontWeight: 600,
    borderRadius: 18,
    width: '49%',
  },
  timeButton: {
    width: 'calc(33.333% - 6px)',
    borderRadius: 18,
    border: '1px solid #ddd',
    color: '#002D55',
    fontWeight: 500,
    padding: '10px 8px',
    fontSize: 12,
  },
  selectedTimeButton: {
    width: 'calc(33.333% - 6px)',
    borderRadius: 18,
    background: '#002D55',
    color: '#fff',
    fontWeight: 600,
    padding: '10px 8px',
    fontSize: 12,
    '&:hover': {
      background: '#002D55',
    },
  },
  helperText: {
    color: '#486d86',
    marginBottom: 12,
  },
  availabilityHint: {
    color: '#486d86',
    marginTop: -8,
    marginBottom: 12,
  },
  detailField: {
    marginBottom: 20,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 16,
    borderTop: '1px solid #e6ebf0',
  },
  serviceSummary: {
    color: '#486d86',
    marginTop: 4,
    marginBottom: 18,
  },
}));

const parseSpecialities = (value) => (
  (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
);

const formatSlot = (value) => {
  if (!value) {
    return 'No slot selected yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const getDateInputValue = (value) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (value, days) => {
  const nextDate = new Date(value);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const formatDayNumber = (value) => value.toLocaleDateString(undefined, { day: 'numeric' });

const formatDayLabel = (value) => value.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();

const formatWeekRange = (dates) => {
  if (!dates.length) {
    return '';
  }

  const firstDate = dates[0].date;
  const lastDate = dates[dates.length - 1].date;
  const sameMonth = firstDate.getMonth() === lastDate.getMonth() && firstDate.getFullYear() === lastDate.getFullYear();

  if (sameMonth) {
    return `${firstDate.toLocaleDateString(undefined, { month: 'long' })} ${firstDate.getFullYear()}`;
  }

  return `${firstDate.toLocaleDateString(undefined, { month: 'short' })} - ${lastDate.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
};

const DATE_CHOICES_PER_VIEW = 14;
const MAX_BOOKING_DAYS_AHEAD = 21;
const MAX_WEEK_OFFSET = Math.max(Math.ceil((MAX_BOOKING_DAYS_AHEAD - DATE_CHOICES_PER_VIEW) / 7), 0);

const buildDateChoices = (weekOffset) => {
  const today = getStartOfToday();
  const startOffset = weekOffset * 7;
  const visibleDays = Math.max(Math.min(DATE_CHOICES_PER_VIEW, MAX_BOOKING_DAYS_AHEAD - startOffset), 0);

  return Array.from({ length: visibleDays }, (_, index) => {
    const date = addDays(today, startOffset + index);
    return {
      key: getDateInputValue(date),
      date,
      isPast: date < today,
    };
  });
};

function PublicProviderBooking() {
  const classes = useStyles();
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const sessionId = query.get('session_id') || '';
  const intakeHandoffId = query.get('intake_handoff_id') || '';
  const assessmentComplete = query.get('assessment_complete') === 'true';
  const bookingFirst = query.get('booking_first') === 'true';
  const lockClinic = query.get('lock_clinic') === 'true';
  const temporaryTestMode = query.get('temporary_test_mode') === 'true';
  const clinicId = query.get('clinic_id') || '';
  const clinicName = query.get('clinic_name') || 'Selected clinic';
  const clinicCity = query.get('city') || '';
  const clinicCountry = query.get('country') || '';
  const careProviderType = query.get('care_provider_type') || '';
  const addressQuery = query.get('address_query') || '';
  const availabilityWindow = query.get('availability_window') || '';
  const availableFrom = query.get('available_from') || '';
  const availableTo = query.get('available_to') || '';
  const requiresInsuranceSupport = query.get('require_insurance_support') === 'true';
  const insuranceNetwork = query.get('insurance_network') || '';
  const requiresPublicSubsidy = query.get('public_subsidy') === 'true';
  const initialLocationId = query.get('clinic_location_id') || '';
  const slotStart = query.get('slot_start') || '';
  const slotEnd = query.get('slot_end') || '';
  const locationName = query.get('location_name') || '';
  const specialities = useMemo(() => parseSpecialities(query.get('specialities')), [query]);
  const recommendationSummary = useMemo(() => readVideoconsultationSummaryFromQuery(query), [query]);
  const recommendationCopy = useMemo(
    () => buildVideoconsultationRecommendationCopy(recommendationSummary),
    [recommendationSummary],
  );
  const [bookingOptions, setBookingOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState(initialLocationId);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [selectedDate, setSelectedDate] = useState(getDateInputValue(slotStart) || getDateInputValue(new Date()));
  const [weekOffset, setWeekOffset] = useState(0);
  const [dateAvailability, setDateAvailability] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availableTimeDetails, setAvailableTimeDetails] = useState([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [timesError, setTimesError] = useState('');
  const [selectedStartIso, setSelectedStartIso] = useState(slotStart || '');
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientLastName, setPatientLastName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [patientNotes, setPatientNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingResult, setBookingResult] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const [emailFallbackUrl, setEmailFallbackUrl] = useState('');

  const handleBackToClinics = () => {
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'virtus-public-booking-back-to-clinics' }, window.location.origin);
      return;
    }

    if (lockClinic && clinicId) {
      const lockedClinicParams = new URLSearchParams();
      lockedClinicParams.set('clinic_id', clinicId);
      lockedClinicParams.set('lock_clinic', 'true');
      if (sessionId) {
        lockedClinicParams.set('session_id', sessionId);
      }
      if (intakeHandoffId) {
        lockedClinicParams.set('intake_handoff_id', intakeHandoffId);
      }
      if (temporaryTestMode) {
        lockedClinicParams.set('temporary_test_mode', 'true');
      }
      if (bookingFirst) {
        lockedClinicParams.set('booking_first', 'true');
      }
      if (clinicName) {
        lockedClinicParams.set('clinic_name', clinicName);
      }
      if (clinicCity) {
        lockedClinicParams.set('city', clinicCity);
      }
      if (clinicCountry) {
        lockedClinicParams.set('country', clinicCountry);
      }
      if (specialities.length > 0) {
        lockedClinicParams.set('specialities', specialities.join(','));
      }
      if (careProviderType) {
        lockedClinicParams.set('care_provider_type', careProviderType);
      }
      if (addressQuery) {
        lockedClinicParams.set('address_query', addressQuery);
      }
      if (availabilityWindow) {
        lockedClinicParams.set('availability_window', availabilityWindow);
      }
      if (availableFrom) {
        lockedClinicParams.set('available_from', availableFrom);
      }
      if (availableTo) {
        lockedClinicParams.set('available_to', availableTo);
      }
      if (requiresInsuranceSupport) {
        lockedClinicParams.set('require_insurance_support', 'true');
      }
      if (insuranceNetwork) {
        lockedClinicParams.set('insurance_network', insuranceNetwork);
      }
      if (requiresPublicSubsidy) {
        lockedClinicParams.set('public_subsidy', 'true');
      }
      appendVideoconsultationSummaryToParams(lockedClinicParams, recommendationSummary);

      window.location.assign(`/public/booking?${lockedClinicParams.toString()}`);
      return;
    }

    const matchParams = new URLSearchParams();
    if (sessionId) {
      matchParams.set('session_id', sessionId);
    }
    if (intakeHandoffId) {
      matchParams.set('intake_handoff_id', intakeHandoffId);
    }
    if (temporaryTestMode) {
      matchParams.set('temporary_test_mode', 'true');
    }
    if (specialities.length > 0) {
      matchParams.set('specialities', specialities.join(','));
    }
    if (clinicCity) {
      matchParams.set('city', clinicCity);
    }
    if (clinicCountry) {
      matchParams.set('country', clinicCountry);
    }
    if (careProviderType) {
      matchParams.set('care_provider_type', careProviderType);
    }
    if (addressQuery) {
      matchParams.set('address_query', addressQuery);
    }
    if (availabilityWindow) {
      matchParams.set('availability_window', availabilityWindow);
    }
    if (availableFrom) {
      matchParams.set('available_from', availableFrom);
    }
    if (availableTo) {
      matchParams.set('available_to', availableTo);
    }
    if (requiresInsuranceSupport) {
      matchParams.set('require_insurance_support', 'true');
    }
    if (insuranceNetwork) {
      matchParams.set('insurance_network', insuranceNetwork);
    }
    if (requiresPublicSubsidy) {
      matchParams.set('public_subsidy', 'true');
    }
    appendVideoconsultationSummaryToParams(matchParams, recommendationSummary);

    const target = matchParams.toString() ? `/public/match?${matchParams.toString()}` : '/public/match';
    window.location.assign(target);
  };

  const recommendationCard = recommendationCopy ? (
    <Box
      style={{
        padding: 20,
        marginBottom: 20,
        borderRadius: 18,
        background: recommendationCopy.background,
        border: `1px solid ${recommendationCopy.border}`,
      }}
    >
      <Typography variant="subtitle1" style={{ color: recommendationCopy.accent, fontWeight: 700, marginBottom: 8 }}>
        {recommendationCopy.title}
      </Typography>
      <Typography variant="body2" style={{ color: '#324158', marginBottom: 12 }}>
        {recommendationCopy.body}
      </Typography>
      {(recommendationSummary?.reportedSpecialities || []).length > 0 && (
        <Box style={{ marginBottom: 10 }}>
          <Typography variant="caption" style={{ color: '#5f6c7b', fontWeight: 700, display: 'block', marginBottom: 6 }}>
            Inferred body areas
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {recommendationSummary.reportedSpecialities.map((item) => (
              <Chip key={`booking-speciality-${item}`} label={formatVideoconsultationSummaryLabel(item)} size="small" />
            ))}
          </Box>
        </Box>
      )}
      {(recommendationSummary?.reportedSymptomTags || []).length > 0 && (
        <Box style={{ marginBottom: (recommendationSummary?.redFlagTags || []).length > 0 ? 10 : 0 }}>
          <Typography variant="caption" style={{ color: '#5f6c7b', fontWeight: 700, display: 'block', marginBottom: 6 }}>
            Inferred symptom tags
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {recommendationSummary.reportedSymptomTags.map((item) => (
              <Chip key={`booking-symptom-${item}`} label={formatVideoconsultationSummaryLabel(item)} size="small" />
            ))}
          </Box>
        </Box>
      )}
      {(recommendationSummary?.redFlagTags || []).length > 0 && (
        <Box>
          <Typography variant="caption" style={{ color: '#5f6c7b', fontWeight: 700, display: 'block', marginBottom: 6 }}>
            Signals flagged for review
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {recommendationSummary.redFlagTags.map((item) => (
              <Chip
                key={`booking-red-flag-${item}`}
                label={formatVideoconsultationSummaryLabel(item)}
                size="small"
                style={{ backgroundColor: '#fff', border: '1px solid #e2b27b' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  ) : null;

  const filteredServices = useMemo(() => {
    const services = bookingOptions?.services || [];
    if (!selectedStaffId) {
      return services;
    }

    return services.filter((service) => {
      const staffMemberIds = Array.isArray(service.staff_member_ids) ? service.staff_member_ids : [];
      return staffMemberIds.length === 0 || staffMemberIds.includes(selectedStaffId);
    });
  }, [bookingOptions, selectedStaffId]);

  const selectedLocation = useMemo(
    () => (bookingOptions?.clinic_locations || []).find((location) => location.id === selectedLocationId),
    [bookingOptions, selectedLocationId],
  );

  const selectedService = useMemo(
    () => (bookingOptions?.services || []).find((service) => service.id === selectedServiceId),
    [bookingOptions, selectedServiceId],
  );

  const selectedStaff = useMemo(
    () => (bookingOptions?.staff || []).find((staff) => staff.user_id === selectedStaffId),
    [bookingOptions, selectedStaffId],
  );

  const selectedTimeOption = useMemo(
    () => availableTimeDetails.find((item) => item.start_datetime === selectedStartIso),
    [availableTimeDetails, selectedStartIso],
  );

  const selectedRoomId = selectedTimeOption?.room_id || '';

  const dateChoices = useMemo(() => buildDateChoices(weekOffset), [weekOffset]);
  const canShowPreviousDates = weekOffset > 0;
  const canShowMoreDates = weekOffset < MAX_WEEK_OFFSET;

  const weekRangeLabel = useMemo(() => formatWeekRange(dateChoices), [dateChoices]);

  const datesWithAvailability = useMemo(
    () => Object.values(dateAvailability).filter((count) => count > 0).length,
    [dateAvailability],
  );

  useEffect(() => {
    if (!intakeHandoffId) {
      return undefined;
    }

    let cancelled = false;
    const fetchIntakeHandoff = async () => {
      try {
        const response = await fetch(`${backendBaseUrl}/question-handler/public-intake/${intakeHandoffId}/`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || data.reason || 'Public intake handoff is unavailable.');
        }
        if (!cancelled) {
          setPatientFirstName((current) => current || data.patient_first_name || '');
          setPatientLastName((current) => current || data.patient_last_name || '');
          setPatientEmail((current) => current || data.patient_email || '');
          setPatientPhone((current) => current || data.patient_phone || '');
        }
      } catch (error) {
        if (!cancelled) {
          setBookingError(error.message || 'Public intake handoff is unavailable.');
        }
      }
    };

    fetchIntakeHandoff();
    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, intakeHandoffId]);

  useEffect(() => {
    if (assessmentComplete || !clinicId) {
      return undefined;
    }

    let cancelled = false;
    const fetchBookingOptions = async () => {
      setOptionsLoading(true);
      setOptionsError('');
      try {
        const params = new URLSearchParams({ care_provider_id: clinicId });
        if (intakeHandoffId) {
          params.set('public_intake_handoff_id', intakeHandoffId);
        }
        if (insuranceNetwork) {
          params.set('insurance_network', insuranceNetwork);
        }
        if (temporaryTestMode) {
          params.set('temporary_test_mode', 'true');
        }
        const response = await fetch(`${backendBaseUrl}/api/calendar/public/booking-options/?${params.toString()}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load booking options.');
        }
        if (!cancelled) {
          setBookingOptions(data);
          setSelectedLocationId((current) => current || data.default_clinic_location_id || '');
          if (data.staff?.length === 1) {
            setSelectedStaffId((current) => current || data.staff[0].user_id);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setOptionsError(error.message || 'Failed to load booking options.');
        }
      } finally {
        if (!cancelled) {
          setOptionsLoading(false);
        }
      }
    };

    fetchBookingOptions();
    return () => {
      cancelled = true;
    };
  }, [assessmentComplete, backendBaseUrl, clinicId, intakeHandoffId, temporaryTestMode]);

  useEffect(() => {
    if (selectedServiceId && !filteredServices.find((service) => service.id === selectedServiceId)) {
      setSelectedServiceId('');
    }
  }, [filteredServices, selectedServiceId]);

  useEffect(() => {
    if (assessmentComplete || !clinicId || !selectedStaffId || !selectedServiceId || !selectedLocationId || dateChoices.length === 0) {
      setDateAvailability({});
      setAvailabilityLoading(false);
      return undefined;
    }

    let cancelled = false;
    const fetchDateAvailability = async () => {
      setAvailabilityLoading(true);
      try {
        const availabilityEntries = await Promise.all(dateChoices.map(async (dateChoice) => {
          const params = new URLSearchParams({
            care_provider_id: clinicId,
            staff_member_id: selectedStaffId,
            service_id: selectedServiceId,
            clinic_location_id: selectedLocationId,
            date: dateChoice.key,
          });

          if (intakeHandoffId) {
            params.set('public_intake_handoff_id', intakeHandoffId);
          }
          if (insuranceNetwork) {
            params.set('insurance_network', insuranceNetwork);
          }

          if (temporaryTestMode) {
            params.set('temporary_test_mode', 'true');
          }

          const response = await fetch(`${backendBaseUrl}/api/calendar/public/available-times/?${params.toString()}`);
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            return [dateChoice.key, 0];
          }

          const details = Array.isArray(data.available_time_details) ? data.available_time_details : [];
          return [dateChoice.key, details.length];
        }));

        if (!cancelled) {
          const nextAvailability = Object.fromEntries(availabilityEntries);
          setDateAvailability(nextAvailability);

          if (!nextAvailability[selectedDate]) {
            const firstAvailableDate = dateChoices.find((dateChoice) => nextAvailability[dateChoice.key] > 0);
            if (firstAvailableDate) {
              setSelectedDate(firstAvailableDate.key);
              setSelectedStartIso('');
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setDateAvailability({});
        }
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    };

    fetchDateAvailability();
    return () => {
      cancelled = true;
    };
  }, [
    assessmentComplete,
    backendBaseUrl,
    clinicId,
    dateChoices,
    selectedDate,
    selectedLocationId,
    selectedServiceId,
    selectedStaffId,
    intakeHandoffId,
    temporaryTestMode,
  ]);

  useEffect(() => {
    if (assessmentComplete || !clinicId || !selectedStaffId || !selectedServiceId || !selectedDate || !selectedLocationId) {
      setAvailableTimeDetails([]);
      setTimesError('');
      return undefined;
    }

    let cancelled = false;
    const fetchAvailableTimes = async () => {
      setTimesLoading(true);
      setTimesError('');
      try {
        const params = new URLSearchParams({
          care_provider_id: clinicId,
          staff_member_id: selectedStaffId,
          service_id: selectedServiceId,
          clinic_location_id: selectedLocationId,
          date: selectedDate,
        });
        if (intakeHandoffId) {
          params.set('public_intake_handoff_id', intakeHandoffId);
        }
        if (insuranceNetwork) {
          params.set('insurance_network', insuranceNetwork);
        }
        if (temporaryTestMode) {
          params.set('temporary_test_mode', 'true');
        }
        const response = await fetch(`${backendBaseUrl}/api/calendar/public/available-times/?${params.toString()}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load available times.');
        }
        if (!cancelled) {
          const details = Array.isArray(data.available_time_details) ? data.available_time_details : [];
          setAvailableTimeDetails(details);
          setSelectedStartIso((current) => {
            const preselected = details.find((item) => item.start_datetime === current);
            if (preselected) {
              return current;
            }

            const slotMatch = slotStart ? details.find((item) => item.start_datetime === slotStart) : null;
            return slotMatch?.start_datetime || '';
          });
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableTimeDetails([]);
          setTimesError(error.message || 'Failed to load available times.');
        }
      } finally {
        if (!cancelled) {
          setTimesLoading(false);
        }
      }
    };

    fetchAvailableTimes();
    return () => {
      cancelled = true;
    };
  }, [assessmentComplete, backendBaseUrl, clinicId, intakeHandoffId, selectedDate, selectedLocationId, selectedServiceId, selectedStaffId, slotStart, temporaryTestMode]);

  const handleCreateBooking = async () => {
    if (!clinicId || !selectedLocationId || !selectedStaffId || !selectedServiceId || !selectedStartIso || !patientFirstName || !patientLastName || !patientEmail) {
      setBookingError('Please complete patient details, location, staff, service, date, and time before booking.');
      return;
    }

    setBookingLoading(true);
    setBookingError('');
    setEmailStatus('');
    setEmailFallbackUrl('');
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/public/appointments/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          care_provider_id: clinicId,
          clinic_location_id: selectedLocationId,
          staff_member_id: selectedStaffId,
          service_id: selectedServiceId,
          start_datetime: selectedStartIso,
          patient_email: patientEmail,
          patient_first_name: patientFirstName,
          patient_last_name: patientLastName,
          patient_phone: patientPhone,
          patient_notes: patientNotes,
          public_intake_handoff_id: intakeHandoffId || undefined,
          booking_first: bookingFirst,
          insurance_network: insuranceNetwork || undefined,
          specialities,
          temporary_test_mode: temporaryTestMode,
          ...(selectedRoomId && { room: selectedRoomId }),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create appointment.');
      }
      setBookingResult(data);
    } catch (error) {
      setBookingError(error.message || 'Failed to create appointment.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSendAssessmentLink = async () => {
    if (!bookingResult?.assessment_url || !patientEmail) {
      setEmailStatus('Enter a valid email address before sending the assessment link.');
      setEmailFallbackUrl('');
      return;
    }

    setEmailLoading(true);
    setEmailStatus('');
    setEmailFallbackUrl('');
    try {
      const response = await fetch(`${backendBaseUrl}/user/public_assessment_link/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_email: patientEmail,
          patient_name: `${patientFirstName} ${patientLastName}`.trim(),
          assessment_url: bookingResult.assessment_url,
          temporary_test_mode: temporaryTestMode,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send assessment link.');
      }
      const fallbackUrl = data.full_assessment_url || data.assessment_url || '';
      if (data.email_sent === false && fallbackUrl) {
        setEmailFallbackUrl(fallbackUrl);
      }
      setEmailStatus(data.message || `Assessment link sent to ${patientEmail}`);
    } catch (error) {
      setEmailFallbackUrl('');
      setEmailStatus(error.message || 'Failed to send assessment link.');
    } finally {
      setEmailLoading(false);
    }
  };

  if (assessmentComplete) {
    return (
      <Box className={classes.root}>
        <Box className={classes.panel}>
          <Card className={classes.card}>
            <CardContent style={{ padding: 28 }}>
              <Typography variant="h4" style={{ color: '#002D55', fontWeight: 700, marginBottom: 12 }}>
                Assessment completed
              </Typography>
              <Typography variant="body1" style={{ color: '#486d86', marginBottom: 20 }}>
                The patient completed the assessment after booking. The selected clinic and slot context stayed attached through the booking-first path.
              </Typography>

              {recommendationCard}

              <Box className={classes.summaryCard} style={{ padding: 20, marginBottom: 20 }}>
                <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Clinic</Typography>
                <Typography variant="body1" style={{ color: '#324158', marginBottom: 12 }}>{clinicName}</Typography>

                <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Location</Typography>
                <Typography variant="body1" style={{ color: '#324158', marginBottom: 12 }}>{locationName || 'To be confirmed'}</Typography>

                <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Booked slot</Typography>
                <Typography variant="body1" style={{ color: '#324158' }}>{formatSlot(slotStart)}</Typography>
              </Box>

              <Button variant="contained" className={classes.primaryButton} onClick={() => window.location.assign('/')}>
                Return to landing page
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (bookingResult?.appointment) {
    const appointment = bookingResult.appointment;
    const isInitialConsultationBooking = String(
      appointment.service_type || selectedService?.service_type || selectedService?.serviceType || ''
    ).toLowerCase() === 'initial_consultation';
    return (
      <Box className={classes.root}>
        <Box className={classes.panel}>
          <Card className={classes.card}>
            <CardContent style={{ padding: 28 }}>
              <Typography variant="h4" style={{ color: '#002D55', fontWeight: 700, marginBottom: 12 }}>
                Appointment booked
              </Typography>
              <Typography variant="body1" style={{ color: '#486d86', marginBottom: 20 }}>
                {bookingFirst && isInitialConsultationBooking
                  ? 'The appointment is now booked successfully. Choose whether to take the pre-visit digital intake now or receive an email with link to answer it later.'
                  : 'The appointment is now confirmed, and a confirmation email has been sent.'}
              </Typography>

              <Box className={classes.summaryCard} style={{ padding: 20, marginBottom: 20 }}>
                <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, marginBottom: 16 }}>
                  {`${appointment.patient_name || `${patientFirstName} ${patientLastName}`.trim() || 'Selected patient'} - ${appointment.service_name || selectedService?.name || 'Selected service'}`}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Clinic</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{appointment.care_provider_name || clinicName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Location</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{appointment.clinic_location_name || selectedLocation?.name || locationName || 'To be confirmed'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Staff</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{appointment.staff_member_name || 'Selected staff member'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Patient name</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{appointment.patient_name || `${patientFirstName} ${patientLastName}`.trim() || 'Selected patient'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Service</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{appointment.service_name || selectedService?.name || 'Selected service'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Start</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{formatSlot(appointment.start_datetime)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>End</Typography>
                    <Typography variant="body1" style={{ color: '#324158' }}>{formatSlot(appointment.end_datetime)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {bookingFirst && isInitialConsultationBooking && bookingResult.assessment_url && (
                <Box style={{ marginBottom: 20 }}>
                  <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 10 }}>
                    Next step: assessment
                  </Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 12 }}>
                    <Button
                      variant="contained"
                      className={classes.primaryButton}
                      onClick={() => window.location.assign(bookingResult.assessment_url)}
                    >
                      Answer assessment now
                    </Button>
                    <Button
                      variant="outlined"
                      className={classes.secondaryButton}
                      onClick={handleSendAssessmentLink}
                      disabled={emailLoading}
                    >
                      {emailLoading ? 'Sending...' : 'Send link by email'}
                    </Button>
                  </Box>
                  {emailStatus && (
                    <Box>
                      <Typography variant="body2" style={{ color: '#486d86' }}>
                        {emailStatus}
                      </Typography>
                      {emailFallbackUrl && (
                        <Box style={{ marginTop: 12 }}>
                          <Typography variant="body2" style={{ color: '#486d86', marginBottom: 12 }}>
                            Local SMTP is unavailable, so use the generated assessment link below for testing.
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Assessment link"
                            value={emailFallbackUrl}
                            InputProps={{ readOnly: true }}
                          />
                          <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 12 }}>
                            <Button
                              variant="contained"
                              className={classes.primaryButton}
                              onClick={() => window.open(emailFallbackUrl, '_blank', 'noopener,noreferrer')}
                            >
                              Open assessment link
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/*<Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                <Button variant="contained" className={classes.primaryButton} onClick={() => window.location.assign('/')}>
                  Return to landing page
                </Button>
                <Button variant="outlined" className={classes.secondaryButton} onClick={() => window.location.reload()}>
                  Book another appointment
                </Button>
              </Box>*/}
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.panel}>
        <Card className={classes.card}>
          <CardContent style={{ padding: 28 }}>
            <Typography variant="h4" style={{ color: '#002D55', fontWeight: 700, marginBottom: 12 }}>
              Book your appointment
            </Typography>
            <Typography variant="body1" style={{ color: '#486d86', marginBottom: 20 }}>
              Select location, staff member, service, and time to create the real appointment. {bookingFirst ? 'After booking, the patient can answer the assessment immediately or receive the booking-linked assessment by email.' : 'Because the patient already completed the assessment, this step only needs the appointment details.'}
            </Typography>

            {recommendationCard}

            <Box className={classes.summaryCard} style={{ padding: 20, marginBottom: 20 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Clinic</Typography>
                  <Typography variant="body1" style={{ color: '#324158' }}>{clinicName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>City</Typography>
                  <Typography variant="body1" style={{ color: '#324158' }}>{clinicCity || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Country</Typography>
                  <Typography variant="body1" style={{ color: '#324158' }}>{clinicCountry || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Matched specialities</Typography>
                  <Typography variant="body1" style={{ color: '#324158' }}>{specialities.length > 0 ? specialities.join(', ') : 'No speciality captured'}</Typography>
                </Grid>
                {(slotStart || slotEnd || locationName) && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Matched location</Typography>
                      <Typography variant="body1" style={{ color: '#324158' }}>{locationName || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>Suggested slot</Typography>
                      <Typography variant="body1" style={{ color: '#324158' }}>{formatSlot(slotStart)}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            {lockClinic && (
              <Typography variant="body2" style={{ color: '#486d86', marginBottom: 20 }}>
                This booking link stays inside {clinicName}. Other clinics are not shown from this flow.
              </Typography>
            )}

            {optionsLoading && (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <CircularProgress style={{ color: '#002D55' }} />
              </Box>
            )}

            {!optionsLoading && optionsError && (
              <Typography variant="body2" style={{ color: '#b23c17', marginBottom: 16 }}>
                {optionsError}
              </Typography>
            )}

            {!optionsLoading && bookingOptions && (
              <>
                <Box className={classes.dialogHeader} style={{ margin: '0 -28px 24px' }}>
                  <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>
                    Appointment Details
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionLabel}>Clinic Location</Typography>
                    <Box className={classes.selectWrapper}>
                      <ArrowDropDownIcon className={classes.selectIcon} />
                      <select
                        className={classes.selectField}
                        value={selectedLocationId}
                        onChange={(event) => {
                          setSelectedLocationId(event.target.value);
                          setSelectedStartIso('');
                        }}
                      >
                        <option value="">Select a location</option>
                        {(bookingOptions.clinic_locations || []).map((location) => (
                          <option key={location.id} value={location.id}>{location.name}</option>
                        ))}
                      </select>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionLabel}>Staff Member</Typography>
                    <Box className={classes.selectWrapper}>
                      <ArrowDropDownIcon className={classes.selectIcon} />
                      <select
                        className={classes.selectField}
                        value={selectedStaffId}
                        onChange={(event) => {
                          setSelectedStaffId(event.target.value);
                          setSelectedStartIso('');
                        }}
                      >
                        <option value="">Select a staff member</option>
                        {(bookingOptions.staff || []).map((staff) => (
                          <option key={staff.user_id} value={staff.user_id}>{staff.name}</option>
                        ))}
                      </select>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionLabel}>Service</Typography>
                    <Box className={classes.selectWrapper}>
                      <ArrowDropDownIcon className={classes.selectIcon} />
                      <select
                        className={classes.selectField}
                        value={selectedServiceId}
                        onChange={(event) => {
                          setSelectedServiceId(event.target.value);
                          setSelectedStartIso('');
                        }}
                      >
                        <option value="">Select a service</option>
                        {filteredServices.length > 0 ? filteredServices.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} ({service.duration_minutes} min)
                          </option>
                        )) : (
                          <option value="" disabled>No online bookable services available</option>
                        )}
                      </select>
                    </Box>
                    {selectedService && (
                      <Typography variant="body2" className={classes.serviceSummary}>
                        Selected service: {selectedService.name} ({selectedService.duration_minutes} min, {selectedService.price} {selectedService.currency})
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box className={classes.dateHeaderRow}>
                      <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>Select Date</Typography>
                      <Box className={classes.dateNavButtons}>
                        <Button
                          size="small"
                          className={classes.navButton}
                          disabled={!canShowPreviousDates}
                          onClick={() => setWeekOffset((current) => current - 1)}
                          style={{ color: canShowPreviousDates ? '#002D55' : '#ccc' }}
                        >
                          {'<'}
                        </Button>
                        <Button
                          size="small"
                          className={classes.navButton}
                          disabled={!canShowMoreDates}
                          onClick={() => setWeekOffset((current) => current + 1)}
                          style={{ color: canShowMoreDates ? '#002D55' : '#ccc' }}
                        >
                          {'>'}
                        </Button>
                      </Box>
                    </Box>
                    {weekRangeLabel && (
                      <Typography variant="body2" className={classes.helperText}>
                        {weekRangeLabel}
                      </Typography>
                    )}
                    {!availabilityLoading && Boolean(selectedStaffId && selectedServiceId && selectedLocationId) && (
                      <Typography variant="body2" className={classes.availabilityHint}>
                        {datesWithAvailability > 0
                          ? `${datesWithAvailability} date${datesWithAvailability === 1 ? '' : 's'} in this view currently have available times.`
                          : 'No dates in this view currently have available times for the selected staff, service, and location.'}
                      </Typography>
                    )}
                    <Box className={classes.dateGrid}>
                      {dateChoices.map((dateChoice) => {
                        const isSelected = dateChoice.key === selectedDate;
                        const hasAvailability = (dateAvailability[dateChoice.key] || 0) > 0;
                        const buttonClasses = [classes.dateButton];

                        if (isSelected) {
                          buttonClasses.push(classes.selectedDateButton);
                        }

                        if (dateChoice.isPast) {
                          buttonClasses.push(classes.disabledDateButton);
                        }

                        return (
                          <Button
                            key={dateChoice.key}
                            disabled={dateChoice.isPast}
                            className={buttonClasses.join(' ')}
                            onClick={() => {
                              setSelectedDate(dateChoice.key);
                              setSelectedStartIso('');
                            }}
                          >
                            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography className={classes.dateNumber} style={{ color: isSelected ? '#FF7F27' : undefined }}>
                                {formatDayNumber(dateChoice.date)}
                              </Typography>
                              <Typography className={classes.dateLabel}>
                                {formatDayLabel(dateChoice.date)}
                              </Typography>
                              {hasAvailability && <Box className={classes.availabilityDot} />}
                            </Box>
                          </Button>
                        );
                      })}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionLabel}>Select Time</Typography>
                    {timesLoading && (
                      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, marginBottom: 24 }}>
                        <CircularProgress style={{ color: '#002D55' }} size={40} />
                      </Box>
                    )}
                    {!timesLoading && timesError && (
                      <Typography variant="body2" style={{ color: '#b23c17', marginBottom: 12 }}>
                        {timesError}
                      </Typography>
                    )}
                    {!timesLoading && !timesError && availableTimeDetails.length === 0 && (
                      <Typography variant="body2" className={classes.helperText}>
                        Choose location, staff member, service, and date to load available times.
                      </Typography>
                    )}
                    {!timesLoading && availableTimeDetails.length > 0 && (
                      <Box className={classes.timeGrid}>
                        {availableTimeDetails.map((timeOption) => (
                          <Button
                            key={timeOption.start_datetime}
                            className={selectedStartIso === timeOption.start_datetime ? classes.selectedTimeButton : classes.timeButton}
                            onClick={() => setSelectedStartIso(timeOption.start_datetime)}
                          >
                            {timeOption.time}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.sectionLabel}>Patient Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First name"
                      variant="outlined"
                      value={patientFirstName}
                      onChange={(event) => setPatientFirstName(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last name"
                      variant="outlined"
                      value={patientLastName}
                      onChange={(event) => setPatientLastName(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      variant="outlined"
                      value={patientEmail}
                      onChange={(event) => setPatientEmail(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      variant="outlined"
                      value={patientPhone}
                      onChange={(event) => setPatientPhone(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Patient notes"
                      variant="outlined"
                      value={patientNotes}
                      onChange={(event) => setPatientNotes(event.target.value)}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {selectedLocation && selectedStaff && !bookingResult && (
              <Typography variant="body2" className={classes.serviceSummary}>
                Booking with {selectedStaff.name} at {selectedLocation.name}.
              </Typography>
            )}

            {sessionId && !bookingFirst && (
              <Typography variant="body2" style={{ color: '#486d86', marginTop: 12 }}>
                Assessment session preserved: {sessionId}
              </Typography>
            )}

            {bookingError && (
              <Typography variant="body2" style={{ color: '#b23c17', marginTop: 16 }}>
                {bookingError}
              </Typography>
            )}

            <Box className={classes.actionRow}>
              <Button
                variant="contained"
                className={classes.primaryButton}
                onClick={handleCreateBooking}
                disabled={optionsLoading || bookingLoading || !bookingOptions}
              >
                {bookingLoading ? 'Booking...' : 'Book appointment'}
              </Button>
              {!lockClinic && (
                <Button
                  variant="outlined"
                  className={classes.secondaryButton}
                  onClick={handleBackToClinics}
                >
                  Back
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default PublicProviderBooking;